// OnboardingReturnBanner — UI-SPEC S4, D-25 transparency (49-16).
//
// A visitor arriving from the WhatsApp in-week variant B link (`ov=b`) is told plainly why we sent
// this store: we and the community missed them. Inline and non-blocking (DD-8: a modal on landing
// would read as a trap after a "we found a store for you" message), at most once per store per
// browser session, never on the server and never inside the moderator embedded preview.
//
// Everything browser-only (URL, sessionStorage, isEmbeddedPreview) is read inside an effect: the
// store page is SSR, so the first render is always null on both sides.
//
// Ownership rule shared with OnboardingReturnCapture: this component only reads and strips `ov`;
// the capture owns `ort` (it POSTs the token), so the two effects never race on a param.
//
// All copy comes from ONBOARDING_COPY.returnBanner (shared), so this file holds no Arabic literals.

import React, { useEffect, useId, useState } from "react";
import { HeartHandshake, X } from "lucide-react";
import { ONBOARDING_COPY } from "@shared/notificationCopy";
import { RETURN_VARIANT_PARAM } from "@shared/onboarding";
import { readReturnParams } from "@/lib/onboardingUi";
import { isEmbeddedPreview } from "@/lib/embeddedPreview";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import type { TrackableStore } from "@/lib/posthog";

const COPY = ONBOARDING_COPY.returnBanner;
const font = "var(--ys-font)";
const SESSION_KEY_PREFIX = "ys_ob_banner_";

const cardStyle: React.CSSProperties = {
  background: "var(--ys-paper)",
  border: "1px solid var(--ys-rule)",
  borderRadius: "var(--ys-r-md)",
  padding: 16,
  marginBottom: 16,
  direction: "rtl",
  fontFamily: font,
  textAlign: "start",
  display: "flex",
  gap: 12,
  alignItems: "flex-start",
};

const titleStyle: React.CSSProperties = {
  fontFamily: font,
  fontSize: 16,
  fontWeight: 600,
  color: "var(--ys-ink)",
  margin: 0,
};

const bodyStyle: React.CSSProperties = {
  fontFamily: font,
  fontSize: 14,
  fontWeight: 400,
  color: "var(--ys-ink-2)",
  margin: "4px 0 0",
};

const primaryBtnStyle: React.CSSProperties = {
  background: "var(--ys-primary)",
  color: "#fff",
  border: "none",
  borderRadius: "var(--ys-r)",
  padding: "8px 16px",
  minHeight: 44,
  fontFamily: font,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};

const ghostBtnStyle: React.CSSProperties = {
  background: "transparent",
  color: "var(--ys-ink-2)",
  border: "none",
  borderRadius: "var(--ys-r)",
  padding: "8px 12px",
  minHeight: 44,
  fontFamily: font,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};

const closeBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "var(--ys-ink-2)",
  width: 44,
  height: 44,
  minWidth: 44,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  marginTop: -12,
  marginInlineEnd: -12,
};

type Props = {
  store: TrackableStore;
  /** The page's existing review CTA path (opens the auth modal with trigger write_review when signed out). */
  onWriteReview: () => void;
  /** Layout wrapper applied only while the banner is visible, so a hidden banner leaves no gap. */
  wrapperClassName?: string;
};

export default function OnboardingReturnBanner({ store, onWriteReview, wrapperClassName }: Props) {
  const [visible, setVisible] = useState(false);
  const { trackOnboarding } = usePostHogTracking();
  const titleId = useId();
  const storeKey = store.slug || store._id || "";

  useEffect(() => {
    if (typeof window === "undefined" || !storeKey) return;
    if (isEmbeddedPreview()) return;
    // ov=b is appended only to signed WhatsApp variant B links (plan 49-12); the token itself is
    // not re-checked here because OnboardingReturnCapture owns `ort`.
    const { variantB } = readReturnParams(window.location.search);
    if (!variantB) return;

    const params = new URLSearchParams(window.location.search);
    params.delete(RETURN_VARIANT_PARAM);
    const qs = params.toString();
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`,
    );

    const sessionKey = `${SESSION_KEY_PREFIX}${storeKey}`;
    try {
      if (window.sessionStorage.getItem(sessionKey)) return;
      window.sessionStorage.setItem(sessionKey, "1");
    } catch {
      // Storage blocked (private mode quotas etc.): still show it once for this page load.
    }
    setVisible(true);
    trackOnboarding.returnBannerShown({ store });
    // Runs once per store; `store` object identity changes on refetch and must not re-trigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeKey]);

  if (!visible) return null;

  const handleDismiss = () => {
    setVisible(false);
    trackOnboarding.returnBannerDismissed({ store });
  };

  const handleWriteReview = () => {
    trackOnboarding.returnBannerReviewClicked({ store });
    onWriteReview();
  };

  return (
    <div className={wrapperClassName}>
      <div role="region" aria-labelledby={titleId} style={cardStyle}>
        <HeartHandshake size={20} color="var(--ys-primary)" aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 id={titleId} style={titleStyle}>
            {COPY.title}
          </h2>
          <p style={bodyStyle}>{COPY.body}</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
            <button type="button" style={primaryBtnStyle} onClick={handleWriteReview}>
              {COPY.cta}
            </button>
            <button type="button" style={ghostBtnStyle} onClick={handleDismiss}>
              {COPY.dismiss}
            </button>
          </div>
        </div>
        <button type="button" style={closeBtnStyle} aria-label={COPY.closeAria} onClick={handleDismiss}>
          <X size={18} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
